/**
 * Auth token store.
 *
 * Holds access + refresh tokens in memory (NOT localStorage — see
 * SECURITY.md: localStorage is accessible to any JS on the page, making it
 * unsuitable for tokens). The access token lives in module memory; the
 * refresh token lives in sessionStorage (tab-scoped, cleared on tab close,
 * not accessible to other origins).
 *
 * NOTE: The API returns the refresh token in the JSON response body only —
 * there is no HttpOnly cookie path. CORS is configured with credentials:false
 * so cookies cannot be sent cross-origin. sessionStorage is used in all
 * environments. The refresh call sends the token in the request body and
 * receives a rotated token in the response body.
 *
 * Concurrent-refresh safety: a single in-flight refresh Promise is shared
 * across all callers so that two simultaneous 401s don't both trigger a
 * rotation (which would cause the second one to present a used token and
 * trigger family revocation / forced logout).
 */

const REFRESH_KEY = 'puntsave_refresh_token';

/*
  Same base as api/client.ts. This used to be a hard-coded '/api/auth/refresh',
  which only exists behind the Vite dev proxy: in production the app is served
  from app.puntsave.com.au and the API lives on api.puntsave.com.au, so every
  refresh hit the static host, got a non-2xx, and `clear()` logged the customer
  out — once per access-token lifetime (15 min) and on every page reload.
  That is the "keeps logging me out mid-session" report of 2026-10-20.
*/
const BASE = import.meta.env.VITE_API_BASE_URL || '/api';

let _accessToken: string | null = null;

/** Shared in-flight refresh promise to prevent concurrent rotation races. */
let _refreshPromise: Promise<boolean> | null = null;

export const tokenStore = {
  setTokens(accessToken: string, refreshToken: string): void {
    _accessToken = accessToken;
    sessionStorage.setItem(REFRESH_KEY, refreshToken);
  },

  getAccessToken(): string | null {
    return _accessToken;
  },

  clear(): void {
    _accessToken = null;
    sessionStorage.removeItem(REFRESH_KEY);
    _refreshPromise = null;
  },

  /**
   * Refresh the access token using the stored refresh token.
   * Safe to call concurrently — all callers share the same in-flight Promise
   * so only one rotation request reaches the API.
   */
  refresh(): Promise<boolean> {
    // Return the existing in-flight refresh if one is already running.
    if (_refreshPromise) return _refreshPromise;

    _refreshPromise = (async () => {
      const refreshToken = sessionStorage.getItem(REFRESH_KEY);
      if (!refreshToken) return false;

      try {
        const resp = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!resp.ok) {
          tokenStore.clear();
          return false;
        }
        const data = (await resp.json()) as { accessToken: string; refreshToken: string };
        tokenStore.setTokens(data.accessToken, data.refreshToken);
        return true;
      } catch {
        tokenStore.clear();
        return false;
      } finally {
        // Release the mutex so the next call after this one gets a fresh attempt.
        _refreshPromise = null;
      }
    })();

    return _refreshPromise;
  },

  isLoggedIn(): boolean {
    return _accessToken !== null;
  },
};
